import React from 'react';
import { useFormContext } from 'react-hook-form';
import Collapse from '@mui/material/Collapse';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import ControlledSelect from '@/components/common/form/ControlledSelect';
import ControlledStartEndDateInputs from '@/components/common/form/ControlledStartEndDateInputs';
import DateType from '@/constants/DateType';
import translate from '@/lib/intl/translate';

const {
  CALENDAR_YTD,
  CUSTOM_DATES,
  LAST_MONTH,
  LAST_6_MONTHS,
  LAST_12_MONTHS,
} = DateType;

const OPTIONS = [
  {
    value: '',
    label: translate('NONE'),
  },
  {
    value: CALENDAR_YTD,
    label: translate('FORMS_DATE_TYPE_CALENDAR_YTD'),
  },
  {
    value: LAST_MONTH,
    label: translate('FORMS_DATE_TYPE_LAST_MONTH'),
  },
  {
    value: LAST_6_MONTHS,
    label: translate('FORMS_DATE_TYPE_LAST_6_MONTHS'),
  },
  {
    value: LAST_12_MONTHS,
    label: translate('FORMS_DATE_TYPE_LAST_12_MONTHS'),
  },
  {
    value: CUSTOM_DATES,
    label: translate('FORMS_DATE_TYPE_CUSTOM_DATES'),
  },
];

export default function ConstituentTimePeriod({
  classes,
  dateTypeName,
  endDateName,
  options,
  startDateName,
}) {
  const {
    control,
    setValue,
    trigger,
    watch,
    formState: { errors },
  } = useFormContext();
  const watchDateType = watch(dateTypeName);

  React.useCallback(() => {
    if (watchDateType === CUSTOM_DATES) {
      setValue(startDateName, '');
      setValue(endDateName, '');
    }
  }, [watchDateType]);

  const isCustomDate = watchDateType === CUSTOM_DATES;

  return (
    <FormControl
      className={classnames(classes.formControl, 'w-full')}
      size="small"
    >
      <InputLabel shrink={!!watchDateType}>
        {translate('FORMS_TIME_PERIOD_LABEL')}
      </InputLabel>
      <ControlledSelect
        className={isCustomDate ? 'mb-4' : ''}
        id="constituent-filter-date-type"
        label={translate('FORMS_TIME_PERIOD_LABEL')}
        name={dateTypeName}
        control={control}
      >
        {options.map(({ label, value }) => (
          <MenuItem className="w-full" key={value} value={value}>
            {label}
          </MenuItem>
        ))}
      </ControlledSelect>

      <Collapse in={isCustomDate}>
        {isCustomDate && (
          <div className="flex flex-wrap">
            <ControlledStartEndDateInputs
              control={control}
              classes={{
                inputRoot: classes.inputRoot,
                endInputContainer: 'flex-grow md:flex-1 md:w-1/2 md:ml-2',
                startInputContainer:
                  'mb-4 flex-grow md:flex-1 md:w-1/2 md:mr-2 md:mb-0',
              }}
              endDateName={endDateName}
              errors={errors}
              id="constituent-filter-date-picker"
              startDateName={startDateName}
              trigger={trigger}
              watch={watch}
            />
          </div>
        )}
      </Collapse>
    </FormControl>
  );
}

ConstituentTimePeriod.propTypes = {
  classes: PropTypes.shape({
    formControl: PropTypes.string,
    inputRoot: PropTypes.string,
  }),
  dateTypeName: PropTypes.string,
  endDateName: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ),
  startDateName: PropTypes.string,
};
ConstituentTimePeriod.defaultProps = {
  classes: {},
  dateTypeName: 'dateType',
  endDateName: 'endDate',
  options: OPTIONS,
  startDateName: 'startDate',
};
